import { type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { FieldSeparator } from '@bliks-io/ui';
import {
  FormInput,
  FormTextarea,
  FormUserSelect,
  FormDepartmentSelect,
  FormSelect
} from '@/components/form/rhf';
import { PROCESS_ROLE_CLASSIFICATIONS } from '@/components/form/process-role-classifications';
import { type AddProcessRequest, type Department } from '@bliks-io/contracts';
import { usePermissions } from '@/hooks/usePermissions';
import { useDepartments } from '@/api/queries/useDepartments';
import { getProcessCategoryOptions } from '@/lib/process-utils';
import { OrgManagerHint } from '@/components/org-manager-hint';

/**
 * A changed department brings its manager as a changed owner, so an edit's
 * changed-fields payload carries both. An unchanged department keeps the stored
 * owner clean, and falls back to its manager only when none is stored (Excel
 * drafts).
 */
function followDepartmentManager(
  form: UseFormReturn<AddProcessRequest>,
  department: Department | undefined
) {
  const departmentChanged = form.getFieldState('department').isDirty;
  if (!departmentChanged) form.resetField('owner');
  if (departmentChanged || !form.getValues('owner')) {
    form.setValue('owner', department?.manager?._id ?? '', { shouldDirty: departmentChanged });
  }
}

interface ProcessFormFieldsProps {
  form: UseFormReturn<AddProcessRequest>;
  /** An edit submits only changed fields, so a preselected department must count as one. */
  isEditing?: boolean;
  /**
   * When true, automatically populates owner from department manager when department changes.
   * When false (default), user manually selects owner from department users.
   */
  autoPopulateOwnerFromManager?: boolean;
  /**
   * When true, the owner and department fields are disabled with a hint message.
   * Used when the user lacks canEditDepartment capability.
   */
  disableOwnerAndDepartment?: boolean;
  /**
   * When false, hides the optional accountable person field
   * (e.g. the bulk-create wizard, which doesn't support it).
   */
  showAccountable?: boolean;
}

/**
 * Shared process form fields component
 * Used by both create-processes and all-processes pages
 * Renders form fields with consistent layout and styling
 */
export function ProcessFormFields({
  form,
  isEditing = false,
  autoPopulateOwnerFromManager = false,
  disableOwnerAndDepartment = false,
  showAccountable = true
}: ProcessFormFieldsProps) {
  const { control, watch, setValue } = form;
  const { t } = useTranslation();
  const { can, authContext } = usePermissions();

  // Department heads who are NOT org admins are restricted to their managed departments
  const isOrgAdmin = can('Organization', 'update');
  const managedDepartmentIds = authContext?.managedDeptIds ?? [];
  const isDeptHead = managedDepartmentIds.length > 0;

  // Determine restriction based on number of managed departments
  let restrictDepartmentId: string | undefined;
  let restrictDepartmentIds: string[] | undefined;
  let disableDepartmentSelect = false;

  if (!isOrgAdmin && isDeptHead) {
    if (managedDepartmentIds.length === 1) {
      // Single department: preselect and disable
      restrictDepartmentId = managedDepartmentIds[0];
      disableDepartmentSelect = true;
    } else {
      // Multiple departments: filter but keep enabled
      restrictDepartmentIds = managedDepartmentIds;
    }
  }

  // Fetch departments for auto-population feature
  const { data: departments } = useDepartments();

  // Watch department field to conditionally enable/filter owner selector
  const selectedDepartment = watch('department');
  const selectedDept = selectedDepartment
    ? departments?.find((d) => d._id === selectedDepartment)
    : undefined;

  // For department heads with a single department.
  useEffect(
    function preselectRestrictedDepartment() {
      if (restrictDepartmentId && !selectedDepartment) {
        setValue('department', restrictDepartmentId, { shouldDirty: isEditing });
      }
    },
    [restrictDepartmentId, selectedDepartment, setValue, isEditing]
  );

  // Only the create wizard turns this on.
  useEffect(
    function populateOwnerFromDepartmentManager() {
      if (autoPopulateOwnerFromManager && departments) followDepartmentManager(form, selectedDept);
    },
    [form, selectedDept, departments, autoPopulateOwnerFromManager]
  );

  // Check if selected department has no manager (for warning display)
  const hasNoManager =
    autoPopulateOwnerFromManager && selectedDepartment && selectedDept && !selectedDept.manager;

  return (
    <div className="flex flex-col gap-8">
      {/* Basic Information Section */}
      <div className="grid grid-cols-1 gap-4">
        <FormInput
          control={control}
          name="name"
          label={t('processes.nameLabel')}
          placeholder={t('processes.namePlaceholder')}
          required
        />
      </div>

      <div className="flex flex-col gap-4">
        <FieldSeparator>{t('organization.departments.relationships')}</FieldSeparator>

        {/* Relationships Section */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormDepartmentSelect
            control={control}
            name="department"
            label={t('processes.department')}
            placeholder={t('processes.departmentPlaceholder')}
            required
            restrictToDepartmentId={restrictDepartmentId}
            restrictToDepartmentIds={restrictDepartmentIds}
            disabled={disableDepartmentSelect || disableOwnerAndDepartment}
          />

          {/* The owner moderates, so an external member may hold the role —
              unlike `accountable` below, which stays internal. */}
          <FormUserSelect
            control={control}
            name="owner"
            label={t('processes.ProcessOwner')}
            placeholder={t('processes.ownerPlaceholder')}
            required
            queryOptions={{ classification: PROCESS_ROLE_CLASSIFICATIONS }}
            disabled={disableOwnerAndDepartment}
          />

          {disableOwnerAndDepartment && (
            <div className="-mt-3 md:col-span-2">
              <OrgManagerHint>{t('processes.ownerAndDepartmentEditHint')}</OrgManagerHint>
            </div>
          )}

          {showAccountable && (
            <FormUserSelect
              control={control}
              name="accountable"
              label={t('processes.Accountable')}
              placeholder={t('processes.accountablePlaceholder')}
              defaultUser={selectedDept?.manager}
              allowClear
              clearLabel={t('processes.accountableClearLabel')}
              onClear={() => setValue('accountable', '', { shouldDirty: true })}
            />
          )}
        </div>
        {hasNoManager && (
          <p className="text-sm text-destructive">
            {t('processes.createProcessesPage.noDepartmentHead')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <FieldSeparator>{t('common.Details')}</FieldSeparator>

        {/* Details Section */}
        <FormTextarea
          control={control}
          name="description"
          label={t('common.Description')}
          placeholder={t('processes.descriptionPlaceholder')}
          rows={4}
          required
        />

        {/* TODO: Review AI Code for feature: Process Map */}
        <FormSelect
          control={control}
          name="category"
          label={t('processes.category')}
          placeholder={t('processes.categoryPlaceholder')}
          options={getProcessCategoryOptions(t)}
          allowClear
        />
      </div>
    </div>
  );
}
